import { AlertTriangle, Info } from "lucide-react";
import { MAX_WRITER_SOURCES, urlCapacity } from "@/lib/sourceLimit";

/**
 * One-line "N of 8" counter shown wherever the editor adds sources
 * (Research idea, Custom Article, Telegram tip). See lib/sourceLimit.js.
 *
 *   count     — URLs that will be sent (checked / listed)
 *   reserved  — slots already taken by the editor's brief / documents
 *   reservedText — optional explanation of `reserved` (reservedLabel())
 *
 * Three states: room left (muted), full (amber), over (amber + how
 * many won't be sent, which only happens when the list was already
 * long before the limit existed, or notes/documents were added after
 * the URLs).
 */
export default function SourceLimitNote({
  count,
  reserved = 0,
  reservedText = "",
  testId = "source-limit-note",
  className = "",
}) {
  const cap = urlCapacity(reserved);
  const used = count + reserved;
  const over = Math.max(0, count - cap);
  const full = used >= MAX_WRITER_SOURCES;

  const base = "flex items-start gap-1.5 text-[11px] leading-snug";
  const tone = full
    ? "text-amber-800"
    : "text-[var(--ink-muted)]";
  const Icon = full ? AlertTriangle : Info;

  let text;
  if (over > 0) {
    text = `${over} source${over === 1 ? "" : "s"} past the limit won't be sent — the writer only takes ${MAX_WRITER_SOURCES}. Uncheck or remove ${over === 1 ? "one" : "some"}.`;
  } else if (full) {
    text = `${Math.min(used, MAX_WRITER_SOURCES)} of ${MAX_WRITER_SOURCES} sources — limit reached. The writer only takes ${MAX_WRITER_SOURCES}; remove or uncheck one to add another.`;
  } else {
    const left = MAX_WRITER_SOURCES - used;
    text = `${used} of ${MAX_WRITER_SOURCES} sources — up to ${MAX_WRITER_SOURCES} can be added (${left} left).`;
  }

  return (
    <div
      data-testid={testId}
      data-state={over > 0 ? "over" : full ? "full" : "ok"}
      className={`${base} ${tone} ${className}`}
    >
      <Icon className="w-3 h-3 mt-0.5 shrink-0" />
      <span>
        {text}
        {reservedText ? ` (${reservedText}.)` : ""}
      </span>
    </div>
  );
}